import { UerChapter } from 'constantsProject';
import type { IErrorHandlingRequestState } from 'features/errorHandling/errorHandlingSlice';
import { RequestStatus } from 'features/request/requestSlice';
import { useRequest } from 'features/request/useRequest';
import { useResetStoreData } from 'features/request/useResetStoreData';
import type { PriceListUerRowWithRatioData } from 'gql/graphql';
import { formatNumberToRu } from 'utils';
import { ExportCSVReport } from 'utils/createCSVReportFile';

import { useCallback, useEffect, useState } from 'react';
import type { UseFormSetValue, UseFormGetValues } from 'react-hook-form';

import { parsedEstimateBasisGql } from '../createOrEditUerQueries';

interface IParseUer {
    reset: (data: Record<string, any>) => void;
    checkboxValues: Record<string, boolean>;
    getValues: UseFormGetValues<Record<string, any>>;
    setValue: UseFormSetValue<Record<string, any>>;
    uerRowData?: PriceListUerRowWithRatioData | null;
    uerId?: string;
    fromWorkOrder?: boolean;
    catalogVersion?: string;
    catalogName?: string;
    isMethodologyUER?: boolean;
}

const WRONG_PARSE_PARAMS_MSG =
    'В загружаемом файле сметы-основания параметры не совпадают с параметрами расценки. Пожалуйста, внесите корректировки и повторно загрузите файл.';
const WRONG_PARSE_PARAMS_CODE_MSG = 'Неверный шифр расценки';
const WRONG_PARSE_PARAMS_UNIT_MSG = 'Неверная ед.изм.';

export const useParseUer = (props: IParseUer) => {
    const {
        reset,
        checkboxValues,
        uerRowData,
        uerId,
        getValues,
        setValue,
        fromWorkOrder,
        catalogVersion,
        catalogName,
        isMethodologyUER,
    } = props;

    const [uerDataParseRequestSuccess, setUerDataParseRequestSuccess] = useState<boolean>(false);
    const [wrongParseUerParameters, setWrongParseUerParameters] = useState<boolean>(false);
    const [wrongParseUerParametersData, setWrongParseUerParametersData] = useState<
        IErrorHandlingRequestState | undefined
    >(undefined);

    const [parsedUerData, parsedUerStatus, , parsedUerRequest] = useRequest({
        requestId: 'parsedCreateUer',
        query: parsedEstimateBasisGql,
        withEffect: false,
    });

    const parsedVersionData = parsedUerData?.uerParsedEstimateBasis?.data?.version;

    const wrongParseUerParametersCreateReport = useCallback(() => {
        ExportCSVReport(wrongParseUerParametersData?.validation);
    }, [wrongParseUerParametersData?.validation]);

    const DOWNLOADABLE_ESTIMATE_MSG = `Загружаемая смета-основание рассчитана по методике (${parsedVersionData}), не соответствующей справочнику расценок, в рамках которого загружен Договор (${catalogName})`;

    useEffect(() => {
        if (parsedUerStatus === RequestStatus.SUCCESS && parsedUerData?.uerParsedEstimateBasis?.result) {
            const { code, unit } = uerRowData || {};
            const {
                code: newCode,
                unit: newUnit,
                chapter,
                version,
            } = parsedUerData?.uerParsedEstimateBasis?.data || {};
            const {
                justification,
                uerClassifier,
                ratioGroupIds,
                ratioGroups,
                ratioIds,
                ratioItems,
                groupsDismantling,
                workKind,
                workCode,
                workUnit,
                workInSum,
                workRatio,
                chapter: originChapter,
            } = getValues();

            if (isMethodologyUER && catalogVersion !== parsedVersionData) {
                setWrongParseUerParameters(true);
                setWrongParseUerParametersData({
                    result: false,
                    buttonText: 'Понятно',
                    message: WRONG_PARSE_PARAMS_MSG,
                    validation: [
                        ...(catalogVersion !== parsedVersionData
                            ? [
                                  {
                                      message: DOWNLOADABLE_ESTIMATE_MSG,
                                  },
                              ]
                            : []),
                    ],
                });
            }

            if (uerId && (code !== newCode || unit !== newUnit)) {
                setWrongParseUerParameters(true);
                setWrongParseUerParametersData({
                    result: false,
                    buttonText: 'Понятно',
                    message: WRONG_PARSE_PARAMS_MSG,
                    validation: [
                        ...(code !== newCode
                            ? [
                                  {
                                      message: WRONG_PARSE_PARAMS_CODE_MSG,
                                  },
                              ]
                            : []),
                        ...(unit !== newUnit
                            ? [
                                  {
                                      message: WRONG_PARSE_PARAMS_UNIT_MSG,
                                  },
                              ]
                            : []),
                    ],
                });
            } else {
                setUerDataParseRequestSuccess(true);
                const prices = parsedUerData?.uerParsedEstimateBasis?.data?.currentPrices.reduce(
                    (
                        acc: Record<string, Record<string, string | number>>,
                        current: Record<string, string | number>,
                    ) => {
                        acc[current.constructionTypeId] = {
                            ...current,
                            unitCostWithOther: formatNumberToRu(current.unitCostWithOther, 2),
                        };

                        return acc;
                    },
                    {},
                );

                let newChapterValue;

                if (fromWorkOrder) {
                    newChapterValue = originChapter;
                } else {
                    newChapterValue = chapter
                        ? {
                              id: chapter,
                              label: UerChapter[chapter],
                          }
                        : { id: 'CHAPTER1_7', label: '1-7' };
                }

                reset({
                    ...parsedUerData?.uerParsedEstimateBasis?.data,
                    ...prices,
                    justification,
                    uerClassifier,
                    ratioGroupIds,
                    ratioGroups,
                    groupsDismantling,
                    ratioIds,
                    ratioItems,
                    chapter: newChapterValue,
                    workKind,
                    workCode,
                    workUnit,
                    workInSum,
                    workRatio,
                    version,
                });
            }
        }
    }, [
        DOWNLOADABLE_ESTIMATE_MSG,
        catalogVersion,
        fromWorkOrder,
        getValues,
        isMethodologyUER,
        parsedUerData?.uerParsedEstimateBasis,
        parsedUerStatus,
        parsedVersionData,
        reset,
        setValue,
        uerId,
        uerRowData,
    ]);

    useEffect(() => {
        const checkedIds = Object.entries(checkboxValues).reduce((acc: any, [key, value]) => {
            if (value) {
                acc = [...acc, key];
            }

            return acc;
        }, []);

        if (checkedIds?.length && parsedUerData?.uerParsedEstimateBasis?.data?.currentPrices) {
            checkedIds.forEach((id: string) => {
                setValue(
                    `${id}.unitCostWithOther`,
                    formatNumberToRu(
                        parsedUerData?.uerParsedEstimateBasis?.data?.currentPrices.find(
                            (currentPriceItem) => currentPriceItem.constructionTypeId === Number(id),
                        )?.unitCostWithOther,
                        2,
                    ),
                );
            });
        }
    }, [checkboxValues, parsedUerData?.uerParsedEstimateBasis?.data?.currentPrices, setValue]);

    const [resetParsedDataHandler] = useResetStoreData({ requestId: 'parsedCreateUer' });

    const onCloseParseErrorModal = useCallback(() => {
        setUerDataParseRequestSuccess(false);
        setWrongParseUerParameters(false);
        setWrongParseUerParametersData(undefined);
        resetParsedDataHandler(null);
    }, [resetParsedDataHandler]);

    return {
        uerDataParseRequestSuccess,
        setUerDataParseRequestSuccess,
        parsedUerData: parsedUerData?.uerParsedEstimateBasis,
        parsedUerStatus,
        parsedUerRequest,
        resetParsedDataHandler,
        onCloseParseErrorModal,
        wrongParseUerParameters,
        wrongParseUerParametersData,
        wrongParseUerParametersCreateReport,
    };
};
